import { useCallback } from 'react';

import { useConfigContext } from '@sorare/core/src/contexts/config';
import type { ConfigQuery_exchangeRate } from '@sorare/core/src/contexts/config/types';
import { isExpiredExchangeRateError } from '@sorare/core/src/contexts/graphql/Provider';
import { Level } from '@sorare/core/src/contexts/snackNotification';
import { notificationsQueue } from '@sorare/core/src/hooks/useNotificationsQueue';
import { sendSafeError } from '@sorare/core/src/lib/error';

type Attempt<T> = (args: {
  exchangeRate?: ConfigQuery_exchangeRate;
  silenceErrors: boolean;
}) => Promise<{ errors: { message: string }[]; result: T }>;

/**
 * Runs a settlement flow, and if the backend rejected the exchange rate id as
 * expired, pulls a live rate and runs the whole flow again on it.
 *
 * The flow has to restart from its `prepare` mutation rather than resend the
 * final one with a newer id: the authorizations were priced with the rejected
 * rate and the approvals are signed against them, so swapping the id in place
 * would settle a trade at a price the user never saw. Re-preparing reprices and
 * re-signs, which is why this takes the flow as a callback.
 *
 * The first attempt runs with the hook's error reporting silenced so a recovery
 * does not flash a failure the user never needed to see. Anything not recovered
 * from is reported here instead.
 */
const notifyErrors = (errors: { message: string }[]) =>
  notificationsQueue.addNotification(
    'errors',
    { errors: errors.map(({ message }) => message).join(', ') },
    { level: Level.ERROR }
  );

export const useRetryOnExpiredExchangeRate = () => {
  const { refreshExchangeRate } = useConfigContext();

  return useCallback(
    async <T>(attempt: Attempt<T>) => {
      const first = await attempt({ silenceErrors: true });

      if (!first.errors.some(isExpiredExchangeRateError)) {
        if (first.errors.length) notifyErrors(first.errors);
        return first.result;
      }

      const exchangeRate = await refreshExchangeRate().catch(() => undefined);
      const retried = await attempt({ exchangeRate, silenceErrors: false });

      if (retried.errors.some(isExpiredExchangeRateError)) {
        // Rejected on a rate that is seconds old, so the client is not merely
        // behind the hourly rotation and refreshing again will not help.
        sendSafeError(
          new Error('Exchange rate rejected as expired after a refresh')
        );
      }
      return retried.result;
    },
    [refreshExchangeRate]
  );
};
